import { useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack, { StackProps } from '@mui/material/Stack';

import Iconify from 'src/components/iconify';

import { UserFilters, UserFilterKey, UserFilterValue } from 'src/type/user';
import { levelLabel, verificationStatusLabel } from './utils';

// ----------------------------------------------------------------------

type Props = StackProps & {
  filters: UserFilters;
  onFilters: (name: UserFilterKey, value: UserFilterValue) => void;
  onResetFilters: VoidFunction;
  results: number;
};

export default function UserTableFiltersResult({
  filters,
  onFilters,
  onResetFilters,
  results,
  ...other
}: Props) {
  const handleRemoveKeyword = useCallback(() => {
    onFilters('name', null);
  }, [onFilters]);

  const handleRemoveLevel = useCallback(() => {
    onFilters('level', 'all');
  }, [filters.level, onFilters]);

  const handleRemoveVerification = useCallback(() => {
    onFilters('verification', 'all');
  }, [filters.verification, onFilters]);

  return (
    <Stack spacing={1.5} {...other}>
      <Box sx={{ typography: 'body2' }}>
        <strong>{results}</strong>
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.25 }}>
          รายการ
        </Box>
      </Box>

      <Stack flexGrow={1} spacing={1} direction="row" flexWrap="wrap" alignItems="center">
        {filters.level !== 'all' && (
          <Block label="ระดับ:">
            <Chip
              label={levelLabel(filters.level)}
              size="small"
              onDelete={() => handleRemoveLevel()}
            />
          </Block>
        )}

        {filters.verification !== 'all' && (
          <Block label="การยืนยันตัวตน:">
            <Chip
              size="small"
              label={verificationStatusLabel(filters.verification)}
              onDelete={handleRemoveVerification}
            />
          </Block>
        )}

        {!!filters.name && (
          <Block label="ชื่อ:">
            <Chip label={filters.name.label} size="small" onDelete={handleRemoveKeyword} />
          </Block>
        )}

        <Button
          color="error"
          onClick={onResetFilters}
          startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        >
          ล้าง
        </Button>
      </Stack>
    </Stack>
  );
}

// ----------------------------------------------------------------------

type BlockProps = StackProps & {
  label: string;
};

function Block({ label, children, sx, ...other }: BlockProps) {
  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={1}
      direction="row"
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 1,
        overflow: 'hidden',
        borderStyle: 'dashed',
        ...sx,
      }}
      {...other}
    >
      <Box component="span" sx={{ typography: 'subtitle2' }}>
        {label}
      </Box>

      <Stack spacing={1} direction="row" flexWrap="wrap">
        {children}
      </Stack>
    </Stack>
  );
}
